import {faTimes} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {boundMethod} from "autobind-decorator";
import {debounce, kebabCase} from "lodash";
import React from "react";
import {IntlContext, IntlShape} from "react-intl";

import {ILocalizedText} from "@translate/models";
import {IDLE_DELAY} from "../models";

function autocomplete(input: string, suggestion: string): string {
    const values = input.split(/[\s_]+/);

    // no duplicates
    if (values.some((x) => x.toLowerCase() === suggestion.toLowerCase())) {
        return "";
    }

    return input.replace(
        /([\s_])?([^\s_]*)$/,
        (_, _input) => (_input ?? "") + suggestion,
    );
}

export interface ITextInputProps {
    id: string;
    text: string;

    className?: string; // additional class names for the input element
    style?: React.CSSProperties;
    focus?: boolean; // mount class with focus in input
    autoWidth?: boolean; // do not use this lightly

    autoComplete?: string;
    datalist?: string[];
    deleteButton?: boolean;
    default4EmptyOutput?: string;
    disabled?: boolean;
    invalidMessage?: string;
    noEmptyOutput?: boolean;
    placeholder?: ILocalizedText;
    readOnly?: boolean;
    required?: boolean;
    title?: string;
    type?: string;

    onChange(text: string, id: string): void;
}

interface ITextInputState {
    text: string;
    previousId: string;
    previousText: string;
}

class TextInput extends React.PureComponent<ITextInputProps, ITextInputState> {
    public readonly state: ITextInputState = {
        previousId: "",
        previousText: "",
        text: "",
    };

    private readonly input = React.createRef<HTMLInputElement>();
    private readonly reset = debounce(this.resetText, IDLE_DELAY);

    public static getDerivedStateFromProps(
        props: ITextInputProps,
        state: ITextInputState,
    ): Partial<ITextInputState> {
        if (
            props.id === state.previousId &&
            props.text === state.previousText
        ) {
            return {};
        }

        return {
            previousId: props.id,
            previousText: props.text,
            text: props.text,
        };
    }

    public get textInput() {
        return this.input.current!;
    }

    private get effective() {
        return this.state.text.trim();
    }

    public componentDidMount() {
        const {invalidMessage, focus} = this.props;
        if (invalidMessage) {
            this.input.current?.setCustomValidity(invalidMessage);
        }

        if (focus) {
            this.input.current?.focus();
        }
    }

    public componentDidUpdate(prev: ITextInputProps) {
        this.reset.cancel();

        const {invalidMessage} = this.props;
        if (invalidMessage === prev.invalidMessage) {
            return;
        }

        this.input.current?.setCustomValidity(invalidMessage ?? "");
    }

    public componentWillUnmount() {
        this.reset.cancel();
    }

    @boundMethod
    public setText(e: React.ChangeEvent<HTMLInputElement>) {
        e.preventDefault();

        this.setState({text: e.target.value});
    }

    @boundMethod
    public clear(e: React.SyntheticEvent) {
        e.preventDefault();

        const {onChange, id} = this.props;
        this.reset();
        onChange("", id);
    }

    @boundMethod
    public commitTextValue(e: React.SyntheticEvent) {
        e.preventDefault();

        const {text, onChange, id, noEmptyOutput, default4EmptyOutput} =
            this.props;

        // do not allow empty inputs if props say so
        if (noEmptyOutput && this.effective === "") {
            if (default4EmptyOutput) {
                onChange(default4EmptyOutput, id);
                return;
            }

            this.resetText();
            return;
        }

        // value changed, notify
        if (this.effective !== text) {
            this.reset();
            onChange(this.effective, id);
            return;
        }

        // value not changed, reset text
        this.resetText();
    }

    @boundMethod
    public handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
        switch (e.key) {
            case "Escape":
                e.preventDefault();

                // note: this triggers after modal closes
                this.resetText();
                break;

            case "Enter":
                this.commitTextValue(e);
                break;
        }
    }

    public render() {
        const {id, deleteButton} = this.props;

        if (!deleteButton) {
            return <IntlContext.Consumer children={this.renderInput} />;
        }

        return (
            <div className="input-group">
                <IntlContext.Consumer children={this.renderInput} />

                <span className="input-group-append">
                    <button
                        type="button"
                        id={"text-delete-" + kebabCase(id)}
                        className="btn btn-sm btn-secondary"
                        aria-label="clear"
                        data-role="clear"
                        tabIndex={-1}
                        onClick={this.clear}
                    >
                        <FontAwesomeIcon icon={faTimes} fixedWidth={true} />
                    </button>
                </span>
            </div>
        );
    }

    @boundMethod
    private renderInput(intl: IntlShape) {
        const {
            autoComplete,
            autoWidth,
            className,
            disabled,
            id,
            placeholder,
            readOnly,
            required,
            style,
            title,
            type,
        } = this.props;
        const {text} = this.state;
        const listId = "text-list-" + kebabCase(id);

        let _style = style;
        if (autoWidth) {
            _style = {..._style, minWidth: text.length + "ch"};
        }

        return (
            <React.Fragment>
                <input
                    ref={this.input}
                    type={type ?? "text"}
                    id={"text-input-" + kebabCase(id)}
                    className={className ?? "form-control"}
                    style={_style}
                    autoComplete={autoComplete ?? "off"}
                    disabled={disabled}
                    list={listId}
                    name={"text-input-" + kebabCase(id)}
                    placeholder={placeholder?.(intl)}
                    readOnly={readOnly}
                    required={required}
                    size={8}
                    title={title}
                    value={text}
                    onChange={this.setText}
                    onBlur={this.commitTextValue}
                    onKeyDown={this.handleKeyDown}
                />
                {this.renderDataList(listId)}
            </React.Fragment>
        );
    }

    private renderDataList(listId: string) {
        const {datalist} = this.props;
        const {text} = this.state;
        if (!datalist?.length) {
            return null;
        }

        return (
            <datalist id={listId}>
                {[...new Set(datalist)]
                    .map((x) => autocomplete(text, x))
                    .filter(Boolean)
                    .map((x) => (
                        <option key={x} value={x} />
                    ))}
            </datalist>
        );
    }

    @boundMethod
    private resetText() {
        this.setState({text: this.state.previousText});
    }
}

export default TextInput;
